import {
  PreScreeningQuestion,
  QuestionType,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  CARDIOVASCULAR_ATTRIBUTES,
  MEASUREMENT_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  HORMONE_ATTRIBUTES,
  CANCER_ATTRIBUTES,
  DIABETES_ATTRIBUTES,
  IMMUNOSUPPRESSION_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for AZASCVD High Risk First Event Study - Cohort B
 * For adults who are at high risk of atherosclerotic cardiovascular disease (ASCVD)
 * such as heart attacks or strokes but have not had one yet
 */
export const azascvdHighRiskScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "biological_sex",
    type: QuestionType.RADIO,
    question: "What is your biological sex?",
    helpText: "Why only these two? Clinical trial registries filter based on sex assigned at birth. Intersex patients should choose whichever option better aligns with their experience.",
    required: true,
    allowBack: true,
    options: [
      { value: "male", label: "Male" },
      { value: "female", label: "Female" },
    ],
    attributeMapping: mappers.value({
      male: [],
      female: [],
    }),
  },

  {
    id: "age_range",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      { value: "under_18", label: "Under 18", disqualify: true },
      {
        value: "18_49",
        label: "18-49",
        disqualify: () => {
          // Disqualify all participants under 50 (both male and female)
          return true;
        }
      },
      {
        value: "50_54",
        label: "50-54",
        disqualify: (allAnswers) => {
          const biologicalSex = allAnswers?.["biological_sex"];
          // Disqualify females under 55
          return biologicalSex === "female";
        }
      },
      { value: "55_64", label: "55-64" },
      { value: "65_plus", label: "65 and older" },
    ],
    disqualifyMessage: "This study requires female participants to be 55 years of age or older, and male participants to be 50 years of age or older.",
    attributeMapping: mappers.value({
      under_18: [],
      "18_49": [],
      "50_54": [],
      "55_64": [],
      "65_plus": [],
    }),
  },

  {
    id: "cardiovascular_history_exclusion",
    type: QuestionType.CHECKBOX,
    question: "Have any of the following happened to you?",
    helpText: "This study is for people at high risk but who have NOT had these major cardiovascular events. Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "heart_attack",
        label: "I have had a heart attack",
        disqualify: true,
      },
      {
        value: "stroke",
        label: "I have had a stroke",
        disqualify: true,
      },
      {
        value: "none",
        label: "None of the above has happened to me",
      },
    ],
    disqualifyMessage: "This study is specifically for participants who have not had a heart attack or stroke.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        heart_attack: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: true },
        ],
        stroke: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: true },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "ldl_cholesterol_check",
    type: QuestionType.RADIO,
    question: "Have you had your LDL cholesterol checked in the past 6 weeks?",
    helpText: "LDL or low density lipoprotein is one of the subtypes of cholesterol, sometimes called 'bad' cholesterol",
    required: true,
    allowBack: true,
    options: [
      {
        value: "not_tested",
        label: "I have not had my LDL tested in the past 6 weeks",
      },
      {
        value: "cannot_recall",
        label: "I cannot recall my most recent result",
      },
      {
        value: "under_60",
        label: "My recent LDL was <60",
        disqualify: true,
      },
      {
        value: "60_to_74",
        label: "My recent LDL was 60–74",
        disqualify: true,
      },
      {
        value: "75_to_99",
        label: "My recent LDL was 75–99",
        disqualify: true,
      },
      {
        value: "100_or_greater",
        label: "My recent LDL was 100 or greater",
      },
    ],
    disqualifyMessage: "This study requires participants who either haven't had recent LDL testing, cannot recall their results, or have LDL levels of 100 or higher.",
    attributeMapping: mappers.value({
      not_tested: [
        { attributeDefinitionId: 26821, radioAttributeValue: "000-6t95z9" },
      ],
      cannot_recall: [
        { attributeDefinitionId: 26821, radioAttributeValue: "001-k5vwt" },
      ],
      under_60: [
        { attributeDefinitionId: 26821, radioAttributeValue: "002-under60" },
      ],
      "60_to_74": [
        { attributeDefinitionId: 26821, radioAttributeValue: "003-prioml" },
      ],
      "75_to_99": [
        { attributeDefinitionId: 26821, radioAttributeValue: "004-75to99" },
      ],
      "100_or_greater": [
        { attributeDefinitionId: 26821, radioAttributeValue: "005-sd2mij" },
      ],
    }),
  },

  {
    id: "high_intensity_statin",
    type: QuestionType.RADIO,
    question: "Are you currently taking either Atorvastatin (Lipitor) 40 mg or higher, or Rosuvastatin (Crestor) 20 mg or higher to manage high cholesterol?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I am taking one of these high-intensity statins",
      },
      {
        value: "no",
        label: "No, I am not taking these high-intensity statins",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study requires participants to be taking high-intensity statins (Atorvastatin 40+ mg or Rosuvastatin 20+ mg).",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 26769, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: 26769, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "coronary_calcium_scan",
    type: QuestionType.RADIO,
    question: "Have you ever had a coronary artery calcium scan performed?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes_over_300", label: "Yes, I have had one checked and my result was >300", skipNextQuestions: 1 },
      { value: "yes_100_to_300", label: "Yes, I have had one checked and my result was 100-300" },
      { value: "yes_under_100", label: "Yes, I have had one checked and my result was <100", disqualify: true },
      { value: "yes_cant_recall", label: "Yes, I have had one checked but I can't recall my result" },
      { value: "no_not_checked", label: "No, I have not had one checked" },
    ],
    disqualifyMessage: "This study requires participants to have a coronary artery calcium score of 100 or higher, or to have not been tested.",
    attributeMapping: mappers.value({
      yes_over_300: [
        { attributeDefinitionId: 26796, radioAttributeValue: "003-fd2oeh" },
      ],
      yes_100_to_300: [
        { attributeDefinitionId: 26796, radioAttributeValue: "002-pwmj7h" },
      ],
      yes_under_100: [
        { attributeDefinitionId: 26796, radioAttributeValue: "001-2ilr8" },
      ],
      yes_cant_recall: [
        { attributeDefinitionId: 26796, radioAttributeValue: "004-e7i3sk" },
      ],
      no_not_checked: [
        { attributeDefinitionId: 26794, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "interim_risk_factors",
    type: QuestionType.CHECKBOX,
    question: "Do any of the following apply to you?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "stenting_procedure",
        label: "I have had a stenting procedure for narrowed arteries anywhere in my body in the past",
      },
      {
        value: "cabg_over_5_years",
        label: "I have had a coronary artery bypass graft (CABG) MORE than 5 years ago",
      },
      {
        value: "ankle_brachial_index",
        label: "I have had my ankle brachial index measured and my result was ≤0.85",
      },
      {
        value: "major_narrowing_multiple_arteries",
        label: "I have major narrowing in more than 1 artery in my heart, neck or limbs",
      },
      {
        value: "diabetes_complications",
        label: "I have diabetes with associated eye, kidney or nerve problems",
        disqualify: (allAnswers) => {
          const riskFactors = allAnswers?.["interim_risk_factors"] as string[];
          const ageRange = allAnswers?.["age_range"];
          const calciumScan = allAnswers?.["coronary_calcium_scan"];

          if (!Array.isArray(riskFactors)) return false;

          // Check if they have any of the first 4 qualifying conditions (Path 1)
          const hasQualifyingConditions = riskFactors.some(factor =>
            factor === "stenting_procedure" ||
            factor === "cabg_over_5_years" ||
            factor === "ankle_brachial_index" ||
            factor === "major_narrowing_multiple_arteries"
          );

          if (hasQualifyingConditions) return false;

          // Path 2a: Diabetes with complications + (CKD OR smoking OR age >65)
          const hasDiabetesComplications = riskFactors.includes("diabetes_complications");
          if (hasDiabetesComplications) {
            const hasAdditionalFactor = (
              ageRange === "65_plus" ||
              riskFactors.includes("chronic_kidney_disease") ||
              riskFactors.includes("smoker")
            );
            if (hasAdditionalFactor) return false; // Qualifies
          }

          // Path 2b: Calcium 100-300 + (CKD OR smoking OR age >65 OR diabetes without complications)
          const hasCalcium100_300 = calciumScan === "yes_100_to_300";
          if (hasCalcium100_300) {
            const hasAdditionalFactor = (
              ageRange === "65_plus" ||
              riskFactors.includes("chronic_kidney_disease") ||
              riskFactors.includes("smoker") ||
              riskFactors.includes("type2_diabetes_no_complications")
            );
            if (hasAdditionalFactor) return false; // Qualifies
          }

          return true; // Disqualify if neither path works
        },
      },
      {
        value: "smoker",
        label: "I am a smoker",
        disqualify: (allAnswers) => {
          const riskFactors = allAnswers?.["interim_risk_factors"] as string[];
          const ageRange = allAnswers?.["age_range"];
          const calciumScan = allAnswers?.["coronary_calcium_scan"];

          if (!Array.isArray(riskFactors)) return false;

          // Check if they have any of the first 4 qualifying conditions (Path 1)
          const hasQualifyingConditions = riskFactors.some(factor =>
            factor === "stenting_procedure" ||
            factor === "cabg_over_5_years" ||
            factor === "ankle_brachial_index" ||
            factor === "major_narrowing_multiple_arteries"
          );

          if (hasQualifyingConditions) return false;

          // Path 2a: Diabetes with complications + (CKD OR smoking OR age >65)
          const hasDiabetesComplications = riskFactors.includes("diabetes_complications");
          if (hasDiabetesComplications) {
            const hasAdditionalFactor = (
              ageRange === "65_plus" ||
              riskFactors.includes("chronic_kidney_disease") ||
              riskFactors.includes("smoker")
            );
            if (hasAdditionalFactor) return false; // Qualifies
          }

          // Path 2b: Calcium 100-300 + (CKD OR smoking OR age >65 OR diabetes without complications)
          const hasCalcium100_300 = calciumScan === "yes_100_to_300";
          if (hasCalcium100_300) {
            const hasAdditionalFactor = (
              ageRange === "65_plus" ||
              riskFactors.includes("chronic_kidney_disease") ||
              riskFactors.includes("smoker") ||
              riskFactors.includes("type2_diabetes_no_complications")
            );
            if (hasAdditionalFactor) return false; // Qualifies
          }

          return true; // Disqualify if neither path works
        },
      },
      {
        value: "chronic_kidney_disease",
        label: "I have chronic kidney disease",
        disqualify: (allAnswers) => {
          const riskFactors = allAnswers?.["interim_risk_factors"] as string[];
          const ageRange = allAnswers?.["age_range"];
          const calciumScan = allAnswers?.["coronary_calcium_scan"];

          if (!Array.isArray(riskFactors)) return false;

          // Check if they have any of the first 4 qualifying conditions (Path 1)
          const hasQualifyingConditions = riskFactors.some(factor =>
            factor === "stenting_procedure" ||
            factor === "cabg_over_5_years" ||
            factor === "ankle_brachial_index" ||
            factor === "major_narrowing_multiple_arteries"
          );

          if (hasQualifyingConditions) return false;

          // Path 2a: Diabetes with complications + (CKD OR smoking OR age >65)
          const hasDiabetesComplications = riskFactors.includes("diabetes_complications");
          if (hasDiabetesComplications) {
            const hasAdditionalFactor = (
              ageRange === "65_plus" ||
              riskFactors.includes("chronic_kidney_disease") ||
              riskFactors.includes("smoker")
            );
            if (hasAdditionalFactor) return false; // Qualifies
          }

          // Path 2b: Calcium 100-300 + (CKD OR smoking OR age >65 OR diabetes without complications)
          const hasCalcium100_300 = calciumScan === "yes_100_to_300";
          if (hasCalcium100_300) {
            const hasAdditionalFactor = (
              ageRange === "65_plus" ||
              riskFactors.includes("chronic_kidney_disease") ||
              riskFactors.includes("smoker") ||
              riskFactors.includes("type2_diabetes_no_complications")
            );
            if (hasAdditionalFactor) return false; // Qualifies
          }

          return true; // Disqualify if neither path works
        },
      },
      {
        value: "type2_diabetes_no_complications",
        label: "I have type 2 diabetes without any complications",
        disqualify: (allAnswers) => {
          const riskFactors = allAnswers?.["interim_risk_factors"] as string[];
          const ageRange = allAnswers?.["age_range"];
          const calciumScan = allAnswers?.["coronary_calcium_scan"];

          if (!Array.isArray(riskFactors)) return false;

          // Check if they have any of the first 4 qualifying conditions (Path 1)
          const hasQualifyingConditions = riskFactors.some(factor =>
            factor === "stenting_procedure" ||
            factor === "cabg_over_5_years" ||
            factor === "ankle_brachial_index" ||
            factor === "major_narrowing_multiple_arteries"
          );

          if (hasQualifyingConditions) return false;

          // Path 2a: Diabetes with complications + (CKD OR smoking OR age >65)
          const hasDiabetesComplications = riskFactors.includes("diabetes_complications");
          if (hasDiabetesComplications) {
            const hasAdditionalFactor = (
              ageRange === "65_plus" ||
              riskFactors.includes("chronic_kidney_disease") ||
              riskFactors.includes("smoker")
            );
            if (hasAdditionalFactor) return false; // Qualifies
          }

          // Path 2b: Calcium 100-300 + (CKD OR smoking OR age >65 OR diabetes without complications)
          const hasCalcium100_300 = calciumScan === "yes_100_to_300";
          if (hasCalcium100_300) {
            const hasAdditionalFactor = (
              ageRange === "65_plus" ||
              riskFactors.includes("chronic_kidney_disease") ||
              riskFactors.includes("smoker") ||
              riskFactors.includes("type2_diabetes_no_complications")
            );
            if (hasAdditionalFactor) return false; // Qualifies
          }

          return true; // Disqualify if neither path works
        },
      },
      {
        value: "none",
        label: "None of the above applies to me",
        disqualify: (allAnswers) => {
          const ageRange = allAnswers?.["age_range"];
          const calciumScan = allAnswers?.["coronary_calcium_scan"];

          // For "none", they can only qualify through Path 2b: calcium 100-300 + age >65
          const hasCalcium100_300 = calciumScan === "yes_100_to_300";
          const hasAge65Plus = ageRange === "65_plus";

          if (hasCalcium100_300 && hasAge65Plus) {
            return false; // Qualifies
          }

          return true; // Disqualify
        },
      },
    ],
    disqualifyMessage: "To qualify for this study, you need either: (1) stenting procedure, CABG >5 years ago, ankle brachial index ≤0.85, or major narrowing in multiple arteries, OR (2a) diabetes with complications plus at least one of: age >65, chronic kidney disease, or smoking, OR (2b) coronary calcium score 100-300 plus at least one of: age >65, chronic kidney disease, smoking, or type 2 diabetes without complications.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        stenting_procedure: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CORONARY_ARTERY_REVASCULARIZATION, booleanAttributeValue: true },
        ],
        cabg_over_5_years: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CORONARY_ARTERY_REVASCULARIZATION, booleanAttributeValue: true },
        ],
        ankle_brachial_index: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PERIPHERAL_ARTERY_DISEASE, booleanAttributeValue: true },
        ],
        major_narrowing_multiple_arteries: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PERIPHERAL_ARTERY_DISEASE, booleanAttributeValue: true },
        ],
        diabetes_complications: [
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETIC_NEPHROPATHY, booleanAttributeValue: true },
        ],
        smoker: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.IS_SMOKER, booleanAttributeValue: true },
        ],
        chronic_kidney_disease: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: true },
        ],
        type2_diabetes_no_complications: [
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: true },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CORONARY_ARTERY_REVASCULARIZATION, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PERIPHERAL_ARTERY_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETIC_NEPHROPATHY, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.IS_SMOKER, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "major_medical_conditions",
    type: QuestionType.CHECKBOX,
    question: "Do any of the following apply to you?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "cancer_recent",
        label: "I have had a cancer diagnosis or treatment within the past 5 years (excluding SCC, BCC, and in-situ cervical cancer)",
        disqualify: true,
      },
      {
        value: "organ_transplant",
        label: "I have had a major organ transplant",
        disqualify: true,
      },
      {
        value: "uncontrolled_diabetes",
        label: "I have uncontrolled diabetes (HbA1c ≥ 9.5%)",
        disqualify: true,
      },
      {
        value: "severe_heart_failure",
        label: "I have severe heart failure",
        disqualify: true,
      },
      {
        value: "end_stage_kidney_disease",
        label: "I have end stage kidney failure or I am on dialysis",
        disqualify: true,
      },
      {
        value: "liver_failure_cirrhosis",
        label: "I have liver failure or cirrhosis",
        disqualify: true,
      },
      {
        value: "hepatitis_b_c",
        label: "I have hepatitis B or hepatitis C",
        disqualify: true,
      },
      {
        value: "pregnancy_breastfeeding",
        label: "I am currently pregnant, breastfeeding, or planning to become pregnant",
        disqualify: true,
      },
      {
        value: "none",
        label: "None of the above is true for me",
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants with these medical conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        cancer_recent: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: true },
        ],
        organ_transplant: [
          { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: true },
        ],
        uncontrolled_diabetes: [
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HBA1C_MORE_THAN_10, booleanAttributeValue: true },
        ],
        severe_heart_failure: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: true },
        ],
        pregnancy_breastfeeding: [
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: true },
        ],
        end_stage_kidney_disease: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: true },
        ],
        liver_failure_cirrhosis: [
          { attributeDefinitionId: 72, booleanAttributeValue: true },
        ],
        hepatitis_b_c: [
          { attributeDefinitionId: 834, booleanAttributeValue: true },
          { attributeDefinitionId: 15040, booleanAttributeValue: true },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: false },
          { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: false },
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HBA1C_MORE_THAN_10, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: false },
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: 72, booleanAttributeValue: false },
          { attributeDefinitionId: 834, booleanAttributeValue: false },
          { attributeDefinitionId: 15040, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "recent_medications",
    type: QuestionType.CHECKBOX,
    question: "Have you recently taken any of the following medications?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "pcsk9_past_3mo",
        label: "I have taken PCSK9 inhibitors (Repatha, Praluent) within the past 3 months",
        disqualify: true,
      },
      {
        value: "gemfibrozil_current",
        label: "I am currently taking gemfibrozil (Lopid)",
        disqualify: true,
      },
      {
        value: "inclisiran_past_18mo",
        label: "I have taken inclisiran (Leqvio) within the past 18 months",
        disqualify: true,
      },
      {
        value: "mipomersen_past_12mo",
        label: "I have taken mipomersen (Kynamro) within the past 12 months",
        disqualify: true,
      },
      {
        value: "lomitapide_past_12mo",
        label: "I have taken lomitapide (Juxtapid) within the past 12 months",
        disqualify: true,
      },
      {
        value: "none",
        label: "None of the above is true for me",
      },
    ],
    disqualifyMessage: "This study requires a washout period from certain cholesterol medications.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        pcsk9_past_3mo: [
          { attributeDefinitionId: 22747, medicationGroupAttributeValue: { hasTaken: true, intervals: [] } },
        ],
        gemfibrozil_current: [
          { attributeDefinitionId: 22272, medicationAttributeValue: { hasTaken: true, intervals: [] } },
        ],
        inclisiran_past_18mo: [
          { attributeDefinitionId: 23568, medicationAttributeValue: { hasTaken: true, intervals: [] } },
        ],
        mipomersen_past_12mo: [
          { attributeDefinitionId: 26699, medicationAttributeValue: { hasTaken: true, intervals: [] } },
        ],
        lomitapide_past_12mo: [
          { attributeDefinitionId: 22806, medicationAttributeValue: { hasTaken: true, intervals: [] } },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: 22747, medicationGroupAttributeValue: { hasTaken: false } },
          { attributeDefinitionId: 22272, medicationAttributeValue: { hasTaken: false } },
          { attributeDefinitionId: 23568, medicationAttributeValue: { hasTaken: false } },
          { attributeDefinitionId: 26699, medicationAttributeValue: { hasTaken: false } },
          { attributeDefinitionId: 22806, medicationAttributeValue: { hasTaken: false } },
        ],
      },
    }),
  },

  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT,
    }),
  },

  {
    ...standardWeightQuestion,
    attributeMapping: mappers.weight({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT,
    }),
  },

  {
    id: "other_clinical_trials",
    type: QuestionType.RADIO,
    question: "Are you currently enrolling in or participating in any other clinical trials?",
    required: true,
    allowBack: true,
    options: [
      { value: "no", label: "No, I am not enrolling in or participating in any other clinical trials" },
      { value: "yes", label: "Yes, I am currently enrolling in or participating in another clinical trial", disqualify: true },
    ],
    disqualifyMessage: "Participants can only participate in one clinical trial at a time.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "inPersonAppointments",
    type: QuestionType.RADIO,
    question: "Can you attend regular in-person study visits over approximately 2-3 years?",
    helpText: "Your local trial site will be located within a reasonable distance from your home, with the exact address provided upon applying to the trial.",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes, I can attend in-person visits" },
      {
        value: "no",
        label: "No, I cannot attend in-person visits",
        disqualify: true,
      },
      {
        value: "discuss",
        label: "I would like to discuss the requirements with the research team",
      },
    ],
    disqualifyMessage: "This study requires regular in-person visits over a 2-3 year period.",
    attributeMapping: mappers.value({
      yes: [],
      no: [],
      discuss: [],
    }),
  },
];